import { useState } from "react";
import { api, errorMessage, unwrap, type EventData } from "../../api/client";
import { formatDateTime, formatHeight } from "../../domain/format";
import { status } from "../../domain/status";
import { fr } from "../../i18n/fr";
import { useOffline } from "../../ui/components";
import type { Selection } from "../editor/EditMap";

/**
 * Restrictions and POIs of the event, with the status of each restriction at the shown time (SW-WS-10), the
 * per-row enable/disable action (SW-RS-13) and the bulk one (SW-WS-13).
 */
export function ObjectList({ event: e, canWrite, at, onSelect, onChanged }: { event: EventData; canWrite: boolean; at: Date; onSelect(s: NonNullable<Selection>): void; onChanged(): void }) {
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const offline = useOffline();
  const restrictions = e.restrictions ?? [];
  const pois = e.pois ?? [];
  const cats = new globalThis.Map((e.poiCategories ?? []).map((c) => [c.id, c]));
  const toggle = (id: string) => setChecked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const bulk = async (ids: string[], enabled: boolean) => {
    setBusy(true); setError(null);
    try {
      unwrap(await api.POST("/events/{id}/restrictions/bulk", { params: { path: { id: e.id } }, body: { ids, enabled } }));
      setChecked(new Set());
      onChanged();
    } catch (x) { setError(errorMessage(x, fr.errors)); } finally { setBusy(false); }
  };

  if (restrictions.length === 0 && pois.length === 0) return <p className="muted small">{fr.workspace.noObjects}</p>;

  return (
    <div className="stack">
      {canWrite && checked.size > 0 && (
        <div className="row wrap small">
          <span>{fr.restriction.selected(checked.size)}</span>
          <button disabled={busy || offline} onClick={() => bulk([...checked], true)}>{fr.restriction.bulkEnable}</button>
          <button disabled={busy || offline} onClick={() => bulk([...checked], false)}>{fr.restriction.bulkDisable}</button>
          <button className="ghost" onClick={() => setChecked(new Set())}>{fr.common.cancel}</button>
        </div>
      )}
      {error && <div className="error small">{error}</div>}
      <ul className="object-list">
        {restrictions.map((r) => {
          const st = status(r, e, at);
          return (
            <li key={r.id} className={r.enabled === false ? "disabled" : ""}>
              {canWrite && <input type="checkbox" checked={checked.has(r.id)} onChange={() => toggle(r.id)} aria-label={fr.common.filter} />}
              <button className="link" onClick={() => onSelect({ type: "restriction", id: r.id })}>
                <span className={"kind-dot " + r.kind + (r.crossable ? " crossable" : "")} />
                <span className="obj-main">{fr.kind[r.kind]}{r.kind === "HEIGHT_LIMIT" && r.maxHeightM != null ? ` ${formatHeight(r.maxHeightM)}` : ""}{r.label ? ` · ${r.label}` : ""}</span>
                <span className="muted small">
                  <span className={"badge st-" + st}>{fr.status[st]}</span>
                  {" "}{r.geometry.type === "Point" ? fr.restriction.point : r.kind === "COURSE" ? fr.restriction.coursePath(r.geometry.coordinates.length) : fr.restriction.path(r.geometry.coordinates.length)}
                  {r.startAt || r.endAt ? ` · ${r.startAt ? formatDateTime(r.startAt) : "…"} → ${r.endAt ? formatDateTime(r.endAt) : fr.restriction.noEnd}` : ""}
                </span>
              </button>
              {canWrite && (
                <button className="small row-action" disabled={busy || offline} title={r.enabled === false ? fr.restriction.enable : fr.restriction.disable}
                  onClick={() => bulk([r.id], r.enabled === false)}>{r.enabled === false ? fr.restriction.enable : fr.restriction.disable}</button>
              )}
            </li>
          );
        })}
        {pois.map((p) => (
          <li key={p.id}>
            {canWrite && <span className="spacer-check" />}
            <button className="link" onClick={() => onSelect({ type: "poi", id: p.id })}>
              <span className="kind-dot poi" style={{ background: cats.get(p.categoryId)?.color ?? "#2f9e44" }} />
              <span className="obj-main">{p.name}</span>
              <span className="muted small">{cats.get(p.categoryId)?.name ?? fr.common.unknown}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
